import { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AppContext } from '@/App';
import { CalendarDays, ClipboardList, Download, FileSpreadsheet, FileUp, Filter } from 'lucide-react';

const importFields = [
  { key: 'employeeCode', label: 'Employee Code', required: true, aliases: ['employee code', 'employeeid', 'employee id'] },
  { key: 'date', label: 'Date', required: true, aliases: ['date', 'work date', 'workday'] },
  { key: 'inTime', label: 'In Time', required: true, aliases: ['in time', 'intime', 'start'] },
  { key: 'outTime', label: 'Out Time', required: true, aliases: ['out time', 'outtime', 'finish'] },
  { key: 'status', label: 'Status', required: false, aliases: ['status', 'status optional', 'attendance'] },
  { key: 'remarks', label: 'Remarks', required: false, aliases: ['remarks', 'remark', 'note'] },
  { key: 'mobile', label: 'Mobile fallback', required: false, aliases: ['mobile', 'phone', 'contact number'] },
];

const normalizeImportHeader = (value) => String(value || '').replace(/[_-]/g, ' ').replace(/[^a-zA-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

const today = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

function AttendancePage() {
  const { API, user } = useContext(AppContext);
  const [tab, setTab] = useState('daily');
  const [records, setRecords] = useState([]);
  const [muster, setMuster] = useState([]);
  const [regularizations, setRegularizations] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [attendancePolicy, setAttendancePolicy] = useState({});
  const [importFile, setImportFile] = useState(null);
  const [importColumns, setImportColumns] = useState([]);
  const [importMapping, setImportMapping] = useState({});
  const [importPreview, setImportPreview] = useState(null);
  const [importMode, setImportMode] = useState('skip');
  const [importLayout, setImportLayout] = useState('daily');
  const [importHistory, setImportHistory] = useState([]);
  const [attendanceImportSettings, setAttendanceImportSettings] = useState({ sourcePriority: ['manual', 'import', 'biometric'], lockedMonths: [] });
  const [lockMonthValue, setLockMonthValue] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [musterMonth, setMusterMonth] = useState(today().slice(0, 7));
  const [dailyPage, setDailyPage] = useState(1);
  const [dailyTotal, setDailyTotal] = useState(0);
  const [refreshCount, setRefreshCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    workDate: today(),
    siteId: user?.siteId || '',
    department: user?.department || '',
  });
  const [form, setForm] = useState({
    employeeId: user?.employeeId || user?.id || '',
    employeeName: user?.name || '',
    siteId: user?.siteId || '',
    department: user?.department || '',
    workDate: '',
    status: '',
    shift: '',
    inTime: '',
    outTime: '',
    remarks: '',
  });

  const isEmployee = user?.role === 'Employee';
  const isAuditor = user?.role === 'Auditor';
  const isAdmin = user?.role === 'Admin';
  const canImport = ['Admin', 'HR'].includes(user?.role);
  const canWrite = ['Admin', 'HR', 'Manager', 'Employee'].includes(user?.role);
  const canReview = ['Admin', 'HR'].includes(user?.role) || user?.role === 'Manager';

  const allowedTabs = useMemo(() => {
    const options = [
      { key: 'daily', label: 'Daily Attendance' },
      { key: 'muster', label: 'Monthly Muster' },
    ];
    if (canWrite) {
      options.push({ key: 'my', label: isEmployee ? 'My Attendance' : 'Manual Entry' });
    }
    if (!isAuditor && canWrite) {
      options.push({ key: 'approvals', label: 'Regularizations' });
    }
    if (canImport) options.push({ key: 'import', label: 'Import Attendance' });
    if (canImport) options.push({ key: 'history', label: 'Import History' });
    return options;
  }, [canImport, canWrite, isAuditor, isEmployee]);

  useEffect(() => {
    if (!user) return;
    setForm((prev) => ({
      ...prev,
      employeeId: user?.employeeId || user?.id || '',
      employeeName: user?.name || '',
      siteId: user?.siteId || prev.siteId || '',
      department: user?.department || prev.department || '',
    }));
  }, [user]);

  useEffect(() => {
    const load = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const dailyParams = {
          workDate: filters.workDate || undefined,
          siteId: filters.siteId || undefined,
          department: filters.department || undefined,
          employeeId: isEmployee ? user.employeeId || user.id : undefined,
          page: dailyPage,
          pageSize: 50,
        };

        const [dailyResponse, musterResponse, regularizationResponse, policyResponse] = await Promise.all([
          axios.get(`${API}/hr/attendance/daily`, { params: dailyParams }),
          axios.get(`${API}/hr/attendance/muster`, {
            params: {
              month: musterMonth,
              siteId: filters.siteId || user.siteId || undefined,
              department: filters.department || user.department || undefined,
              employeeId: isEmployee ? user.employeeId || user.id : undefined,
            },
          }),
          axios.get(`${API}/hr/attendance/regularizations`, {
            params: {
              employeeId: isEmployee ? user.employeeId || user.id : undefined,
            },
          }),
          axios.get(`${API}/hr/attendance/policy`, {
            params: { siteId: filters.siteId || user.siteId || undefined, workDate: filters.workDate || undefined },
          }),
        ]);

        setRecords(dailyResponse.data?.items || []);
        setDailyTotal(dailyResponse.data?.total || 0);
        setMuster(musterResponse.data?.items || []);
        setRegularizations(regularizationResponse.data?.items || []);
        const configuredStatuses = policyResponse.data?.statusCodes || [];
        setStatusOptions(configuredStatuses);
        setAttendancePolicy(policyResponse.data?.policy || {});
        setForm((prev) => ({
          ...prev,
          siteId: filters.siteId || user.siteId || prev.siteId,
          department: filters.department || user.department || prev.department,
          status: configuredStatuses.includes(prev.status) ? prev.status : configuredStatuses[0] || '',
        }));
      } catch (error) {
        console.error('Attendance load failed', error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [API, dailyPage, filters, isEmployee, musterMonth, refreshCount, user]);

  useEffect(() => {
    if (!canImport || tab !== 'history') return;
    const loadHistory = async () => {
      try {
        const [historyResponse, settingsResponse] = await Promise.all([
          axios.get(`${API}/hr/attendance/import/batches`),
          isAdmin ? axios.get(`${API}/hr/settings/attendance-import`) : Promise.resolve(null),
        ]);
        setImportHistory(historyResponse.data?.items || []);
        if (settingsResponse?.data) setAttendanceImportSettings(settingsResponse.data);
      } catch (error) {
        alert(error?.response?.data?.detail || 'Could not load attendance import history');
      }
    };
    loadHistory();
  }, [API, canImport, isAdmin, tab]);

  const saveAttendanceImportSettings = async (nextSettings = attendanceImportSettings) => {
    try {
      const response = await axios.put(`${API}/hr/settings/attendance-import`, nextSettings);
      setAttendanceImportSettings(response.data);
    } catch (error) {
      alert(error?.response?.data?.detail || 'Could not save attendance import settings');
    }
  };

  const undoAttendanceImport = async (batch) => {
    if (!window.confirm(`Undo batch ${batch.fileName || batch.id}? ${batch.summary?.committed || 0} attendance rows will be restored or removed.`)) return;
    try {
      await axios.post(`${API}/hr/attendance/import/batches/${batch.id}/undo`);
      const response = await axios.get(`${API}/hr/attendance/import/batches`);
      setImportHistory(response.data?.items || []);
      setRefreshCount((count) => count + 1);
    } catch (error) {
      alert(error?.response?.data?.detail || 'Could not undo attendance import');
    }
  };

  const handleManualMark = async (event) => {
    event.preventDefault();
    if (!user || isAuditor) return;

    try {
      await axios.post(`${API}/hr/attendance/manual`, {
        ...form,
        employeeId: isEmployee ? user.employeeId || user.id : form.employeeId,
        employeeName: isEmployee ? user.name : form.employeeName,
        siteId: form.siteId || filters.siteId || user.siteId,
        department: form.department || filters.department || user.department,
      });
      setForm((prev) => ({ ...prev, workDate: '', shift: '', inTime: '', outTime: '', remarks: '' }));
      setRefreshCount((count) => count + 1);
    } catch (error) {
      console.error('Manual attendance failed', error);
      alert(error?.response?.data?.detail || 'Failed to save attendance');
    }
  };

  const handleRegularization = async (event) => {
    event.preventDefault();
    if (!user || isAuditor) return;

    try {
      await axios.post(`${API}/hr/attendance/regularizations`, {
        employeeId: isEmployee ? user.employeeId || user.id : form.employeeId,
        employeeName: isEmployee ? user.name : form.employeeName,
        siteId: form.siteId || filters.siteId || user.siteId,
        department: form.department || filters.department || user.department,
        workDate: form.workDate,
        requestedStatus: form.status,
        reason: form.remarks || 'Manual correction request',
      });
      alert('Regularization request created');
      setForm((prev) => ({ ...prev, remarks: '' }));
      setRefreshCount((count) => count + 1);
    } catch (error) {
      console.error('Regularization failed', error);
      alert(error?.response?.data?.detail || 'Failed to request regularization');
    }
  };

  const handleDecision = async (id, status) => {
    if (!user || isAuditor) return;
    try {
      await axios.put(`${API}/hr/attendance/regularizations/${id}`, { status, decisionNote: `${status} from UI` });
      setRefreshCount((count) => count + 1);
    } catch (error) {
      console.error('Decision failed', error);
      alert(error?.response?.data?.detail || 'Failed to update approval');
    }
  };

  const exportMuster = async () => {
    try {
      const response = await axios.get(`${API}/hr/attendance/muster/export`, {
        params: {
          month: musterMonth,
          siteId: filters.siteId || undefined,
          department: filters.department || undefined,
          employeeId: isEmployee ? user.employeeId || user.id : undefined,
        },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `attendance-muster-${musterMonth}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Muster export failed', error);
      alert(error?.response?.data?.detail || 'Failed to export muster');
    }
  };

  const downloadImportFile = async (path, filename) => {
    try {
      const response = await axios.get(`${API}${path}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to download the import file');
    }
  };

  const inspectImportColumns = async () => {
    if (!importFile) return;
    setImportBusy(true);
    setImportPreview(null);
    try {
      const data = new FormData();
      data.append('file', importFile);
      data.append('layout', importLayout);
      const response = await axios.post(`${API}/hr/attendance/import/columns`, data);
      const columns = response.data?.columns || [];
      setImportColumns(columns);
      const fields = importLayout === 'muster' ? importFields.filter((field) => ['employeeCode', 'mobile'].includes(field.key)) : importFields;
      setImportMapping(Object.fromEntries(fields.map((field) => {
        const selected = columns.find((column) => field.aliases.includes(normalizeImportHeader(column)));
        return [field.key, selected || ''];
      })));
    } catch (error) {
      alert(error?.response?.data?.detail || 'Could not read the file headers');
    } finally {
      setImportBusy(false);
    }
  };

  const previewImport = async () => {
    if (!importFile) return;
    setImportBusy(true);
    try {
      const data = new FormData();
      data.append('file', importFile);
      data.append('mapping', JSON.stringify(Object.fromEntries(Object.entries(importMapping).filter(([, value]) => value))));
      data.append('layout', importLayout);
      const response = await axios.post(`${API}/hr/attendance/import/preview`, data);
      setImportPreview(response.data);
    } catch (error) {
      alert(error?.response?.data?.detail || 'Attendance preview failed');
    } finally {
      setImportBusy(false);
    }
  };

  const commitImport = async () => {
    if (!importPreview?.batchId) return;
    const counts = importPreview.summary || {};
    const confirmed = window.confirm(`Commit attendance import?\nValid: ${counts.valid || 0}\nExisting conflicts: ${counts.duplicate || 0}\nErrors: ${counts.error || 0}\nMode: ${importMode.replace('_', ' ')}`);
    if (!confirmed) return;
    setImportBusy(true);
    try {
      const response = await axios.post(`${API}/hr/attendance/import/commit`, {
        batchId: importPreview.batchId,
        mode: importMode,
      });
      setImportPreview((preview) => ({ ...preview, commitResult: response.data }));
      setRefreshCount((count) => count + 1);
    } catch (error) {
      alert(error?.response?.data?.detail || 'Attendance import commit failed');
    } finally {
      setImportBusy(false);
    }
  };

  const renderImport = () => (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-4">
        <div className="flex items-center gap-2">
          <FileSpreadsheet size={19} className="text-emerald-700" />
          <h2 className="text-lg font-semibold text-gray-900">Attendance Import</h2>
        </div>
        <button type="button" onClick={() => downloadImportFile(`/hr/attendance/import/template?layout=${importLayout}`, `attendance-import-${importLayout}-template.xlsx`)} className="inline-flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 text-sm hover:bg-gray-50">
          <Download size={16} /> Download template
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-[180px_minmax(240px,1fr)_auto] md:items-end">
        <label className="block text-sm font-medium text-gray-700">
          Layout
          <select value={importLayout} onChange={(event) => {
            setImportLayout(event.target.value);
            setImportColumns([]);
            setImportMapping({});
            setImportPreview(null);
          }} className="mt-1 block w-full rounded border border-gray-300 p-2 text-sm">
            <option value="daily">One row per day</option>
            <option value="muster">Monthly muster grid</option>
          </select>
        </label>
        <label className="block text-sm font-medium text-gray-700">
          Excel, XLS or CSV file
          <input type="file" accept=".xlsx,.xls,.csv" onChange={(event) => {
            setImportFile(event.target.files?.[0] || null);
            setImportColumns([]);
            setImportPreview(null);
          }} className="mt-1 block w-full rounded border border-gray-300 p-2 text-sm" />
        </label>
        <button type="button" onClick={inspectImportColumns} disabled={!importFile || importBusy} className="inline-flex items-center justify-center gap-2 bg-gray-800 text-white rounded-lg px-4 py-2 text-sm disabled:opacity-50">
          <FileUp size={16} /> Read columns
        </button>
      </div>

      {importColumns.length > 0 && (
        <div className="border-t border-gray-200 pt-4">
          <h3 className="font-medium text-gray-900">Column mapping</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(importLayout === 'muster' ? importFields.filter((field) => ['employeeCode', 'mobile'].includes(field.key)) : importFields).map((field) => (
              <label key={field.key} className="block text-sm text-gray-700">
                {field.label}{field.required ? ' *' : ''}
                <select value={importMapping[field.key] || ''} onChange={(event) => setImportMapping((current) => ({ ...current, [field.key]: event.target.value }))} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2">
                  <option value="">{field.required ? 'Select a column' : 'Not mapped'}</option>
                  {importColumns.map((column, index) => <option key={`${column}-${index}`} value={column}>{column || `Column ${index + 1}`}</option>)}
                </select>
              </label>
            ))}
          </div>
          <button type="button" onClick={previewImport} disabled={importBusy || (importLayout === 'daily' ? importFields : importFields.filter((field) => field.key === 'employeeCode')).some((field) => field.required && !importMapping[field.key])} className="mt-4 inline-flex items-center gap-2 bg-[#1a237e] text-white rounded-lg px-4 py-2 text-sm disabled:opacity-50">
            Preview import
          </button>
        </div>
      )}

      {importPreview && (
        <div className="border-t border-gray-200 pt-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-medium text-gray-900">Preview results</h3>
            {!importPreview.alreadyCommitted && (
              <div className="flex items-center gap-2">
                <label htmlFor="import-mode" className="text-sm text-gray-700">Existing entry</label>
                <select id="import-mode" value={importMode} onChange={(event) => setImportMode(event.target.value)} className="rounded border border-gray-300 px-2 py-1.5 text-sm">
                  <option value="skip">Skip</option>
                  <option value="overwrite">Overwrite</option>
                  <option value="fill_blanks">Fill blanks only</option>
                </select>
                <button type="button" onClick={commitImport} disabled={importBusy || !(importPreview.summary?.valid || importPreview.summary?.duplicate)} className="rounded-lg bg-emerald-700 px-4 py-2 text-sm text-white disabled:opacity-50">
                  Commit import
                </button>
              </div>
            )}
          </div>
          <dl className="grid grid-cols-3 gap-3 text-sm">
            {['valid', 'duplicate', 'error'].map((key) => (
              <div key={key} className="border-l-2 border-gray-300 pl-3">
                <dt className="capitalize text-gray-500">{key}</dt>
                <dd className="text-lg font-semibold text-gray-900">{importPreview.summary?.[key] || 0}</dd>
              </div>
            ))}
          </dl>
          {importPreview.commitResult && <p className="text-sm text-emerald-800">Committed {importPreview.commitResult.committed}; skipped {importPreview.commitResult.skipped}.</p>}
          {importPreview.rows?.some((row) => row.warnings?.length) && <p className="text-sm text-amber-800">Warnings: one or more rows fall on approved leave or a holiday. Review row details before committing.</p>}
          {importPreview.unmatched?.length > 0 && importPreview.batchId && (
            <button type="button" onClick={() => downloadImportFile(`/hr/attendance/import/batches/${importPreview.batchId}/unmatched/export`, `attendance-unmatched-${importPreview.batchId}.csv`)} className="inline-flex items-center gap-2 border border-amber-300 rounded-lg px-3 py-2 text-sm hover:bg-amber-50">
              <Download size={16} /> Download unmatched list ({importPreview.unmatched.length})
            </button>
          )}
          {importPreview.batchId && importPreview.summary?.error > 0 && (
            <button type="button" onClick={() => downloadImportFile(`/hr/attendance/import/batches/${importPreview.batchId}/errors/export`, `attendance-import-errors-${importPreview.batchId}.xlsx`)} className="inline-flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 text-sm hover:bg-gray-50">
              <Download size={16} /> Download row errors
            </button>
          )}
          {importPreview.errors?.length > 0 && (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-600"><tr><th className="px-3 py-2">Row</th><th className="px-3 py-2">Employee</th><th className="px-3 py-2">Issue</th></tr></thead>
                <tbody className="divide-y divide-gray-100">{importPreview.errors.map((error) => <tr key={`${error.rowNumber}-${error.errorType}`}><td className="px-3 py-2">{error.rowNumber}</td><td className="px-3 py-2">{error.employeeCode || '—'}</td><td className="px-3 py-2">{error.error}</td></tr>)}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );

  const renderImportHistory = () => (
    <section className="space-y-5">
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">
        <h2 className="text-lg font-semibold text-gray-900">Attendance import history</h2>
        <button type="button" onClick={async () => {
          const response = await axios.get(`${API}/hr/attendance/import/batches`);
          setImportHistory(response.data?.items || []);
        }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm hover:bg-gray-50">Refresh</button>
      </div>
      {isAdmin && (
        <div className="grid gap-5 border-b border-gray-200 pb-5 lg:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Source priority</label>
            <select value={attendanceImportSettings.sourcePriority.join(',')} onChange={(event) => setAttendanceImportSettings((current) => ({ ...current, sourcePriority: event.target.value.split(',') }))} className="mt-1 block w-full rounded border border-gray-300 p-2 text-sm">
              <option value="manual,import,biometric">Manual correction, then import, then biometric</option>
              <option value="manual,biometric,import">Manual correction, then biometric, then import</option>
              <option value="import,manual,biometric">Import, then manual correction, then biometric</option>
              <option value="import,biometric,manual">Import, then biometric, then manual correction</option>
              <option value="biometric,manual,import">Biometric, then manual correction, then import</option>
              <option value="biometric,import,manual">Biometric, then import, then manual correction</option>
            </select>
            <button type="button" onClick={() => saveAttendanceImportSettings()} className="mt-2 rounded bg-gray-800 px-3 py-2 text-sm text-white">Save priority</button>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Lock attendance month</label>
            <div className="mt-1 flex gap-2">
              <input type="month" value={lockMonthValue} onChange={(event) => setLockMonthValue(event.target.value)} className="min-w-0 flex-1 rounded border border-gray-300 p-2 text-sm" />
              <button type="button" disabled={!lockMonthValue} onClick={() => {
                const nextSettings = { ...attendanceImportSettings, lockedMonths: [...new Set([...(attendanceImportSettings.lockedMonths || []), lockMonthValue])].sort() };
                setAttendanceImportSettings(nextSettings);
                setLockMonthValue('');
                saveAttendanceImportSettings(nextSettings);
              }} className="rounded bg-gray-800 px-3 py-2 text-sm text-white disabled:opacity-50">Lock</button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {(attendanceImportSettings.lockedMonths || []).map((month) => <button key={month} type="button" onClick={() => {
                const nextSettings = { ...attendanceImportSettings, lockedMonths: attendanceImportSettings.lockedMonths.filter((item) => item !== month) };
                setAttendanceImportSettings(nextSettings);
                saveAttendanceImportSettings(nextSettings);
              }} className="border border-gray-300 rounded px-2 py-1 text-xs">{month} · unlock</button>)}
            </div>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-gray-600"><tr><th className="px-3 py-2">File</th><th className="px-3 py-2">Uploaded by</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Counts</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Actions</th></tr></thead>
          <tbody className="divide-y divide-gray-100">
            {importHistory.map((batch) => (
              <tr key={batch.id}>
                <td className="px-3 py-2">{batch.fileName}<span className="block text-xs text-gray-500">{batch.layout || 'daily'}</span></td>
                <td className="px-3 py-2">{batch.createdByName || batch.createdBy || 'Unknown'}</td>
                <td className="px-3 py-2">{batch.createdAt ? new Date(batch.createdAt).toLocaleString() : '—'}</td>
                <td className="px-3 py-2">Valid {batch.summary?.valid || 0} · Conflicts {batch.summary?.duplicate || 0} · Errors {batch.summary?.error || 0}</td>
                <td className="px-3 py-2 capitalize">{batch.status}</td>
                <td className="px-3 py-2"><div className="flex flex-wrap gap-2">
                  {(batch.summary?.error || 0) > 0 && <button type="button" onClick={() => downloadImportFile(`/hr/attendance/import/batches/${batch.id}/errors/export`, `attendance-import-errors-${batch.id}.xlsx`)} className="text-blue-800 underline">Errors</button>}
                  {batch.status === 'committed' && <button type="button" onClick={() => undoAttendanceImport(batch)} className="text-red-700 underline">Undo</button>}
                </div></td>
              </tr>
            ))}
            {!importHistory.length && <tr><td colSpan="6" className="px-3 py-6 text-center text-gray-500">No attendance import batches.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );

  const renderDailyGrid = () => (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Filter className="text-gray-400" size={18} />
          <input
            type="date"
            value={filters.workDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, workDate: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <input
            value={filters.siteId}
            placeholder="Site"
            onChange={(e) => setFilters((prev) => ({ ...prev, siteId: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <input
            value={filters.department}
            placeholder="Department"
            onChange={(e) => setFilters((prev) => ({ ...prev, department: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">Employee</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">Site</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">Department</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">Shift</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {records.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-500">No attendance entries yet.</td>
              </tr>
            ) : (
              records.map((item) => (
                <tr key={item.id || `${item.employeeId}-${item.workDate}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.employeeName || item.employeeId}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.workDate}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.siteId}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.department}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">{item.status || 'Unmarked'}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.shift || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm text-gray-600">
        <span>{dailyTotal} attendance rows</span>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setDailyPage((page) => Math.max(1, page - 1))} disabled={dailyPage === 1} className="border border-gray-300 rounded px-3 py-1 disabled:opacity-40">Previous</button>
          <span>Page {dailyPage} of {Math.max(1, Math.ceil(dailyTotal / 50))}</span>
          <button type="button" onClick={() => setDailyPage((page) => page + 1)} disabled={dailyPage * 50 >= dailyTotal} className="border border-gray-300 rounded px-3 py-1 disabled:opacity-40">Next</button>
        </div>
      </div>
    </div>
  );

  const renderMuster = () => (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <ClipboardList className="text-[#1a237e]" size={20} />
          <h3 className="font-semibold text-gray-900">Monthly Muster</h3>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={musterMonth} onChange={(event) => setMusterMonth(event.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          <button type="button" onClick={exportMuster} title="Download Excel muster" aria-label="Download Excel muster" className="inline-flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 text-sm hover:bg-gray-50">
            <Download size={16} /> Excel
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">Employee</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">Department</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">Status Summary</th>
            </tr>
          </thead>
          <tbody>
            {muster.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-10 text-center text-gray-500">No muster data for the current month.</td>
              </tr>
            ) : (
              muster.map((row) => (
                <tr key={row.employeeId || row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.employeeName || row.employeeId}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{row.department || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {Object.entries(row.counts || {}).map(([status, count]) => (
                      <span key={status} className="inline-flex mr-2 mt-1 px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">
                        {status}: {count}
                      </span>
                    ))}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderPolicyThresholds = () => {
    const thresholds = Object.entries(attendancePolicy).filter(([key, value]) => /threshold|grace/i.test(key) && ['string', 'number', 'boolean'].includes(typeof value));
    if (!thresholds.length) return null;
    return (
      <p className="md:col-span-2 text-sm text-gray-600">
        Policy thresholds: {thresholds.map(([key, value]) => `${key}: ${value}`).join(' · ')}
      </p>
    );
  };

  const renderApprovals = () => (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
      {regularizations.length === 0 ? (
        <p className="text-gray-600">No regularization requests found.</p>
      ) : (
        regularizations.map((item) => (
          <div key={item.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="font-semibold text-gray-900">{item.employeeName || item.employeeId}</p>
                <p className="text-sm text-gray-600">{item.workDate} • {item.department}</p>
              </div>
              <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">{item.status}</span>
            </div>
            <p className="mt-2 text-sm text-gray-700">Requested status: {item.requestedStatus}</p>
            <p className="text-sm text-gray-700">Reason: {item.reason || 'No reason provided'}</p>
            {canReview && item.status === 'Pending' && (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => handleDecision(item.id, 'Approved')}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                >
                  Approve
                </button>
                <button
                  onClick={() => handleDecision(item.id, 'Rejected')}
                  className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-500">Loading attendance…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><CalendarDays className="text-[#1a237e]" size={28} /> Manual Attendance</h1>
          <p className="text-gray-600 mt-1">Daily accrual, muster reporting and regularizations for HR operations.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {allowedTabs.map((entry) => (
          <button
            key={entry.key}
            onClick={() => setTab(entry.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === entry.key ? 'bg-[#1a237e] text-white' : 'bg-white text-gray-700 border border-gray-200'}`}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'daily' && renderDailyGrid()}
      {tab === 'muster' && renderMuster()}
      {tab === 'my' && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
          <form onSubmit={handleManualMark} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isEmployee ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                <input value={user?.name || ''} readOnly className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50" />
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
                  <input value={form.employeeId} onChange={(e) => setForm((prev) => ({ ...prev, employeeId: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee name</label>
                  <input value={form.employeeName} onChange={(e) => setForm((prev) => ({ ...prev, employeeName: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2" required />
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input type="date" value={form.workDate} onChange={(e) => setForm((prev) => ({ ...prev, workDate: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2" required disabled={!statusOptions.length}>
                {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shift</label>
              <input value={form.shift} onChange={(e) => setForm((prev) => ({ ...prev, shift: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="A / B / General" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">In time</label>
              <input type="time" value={form.inTime} onChange={(e) => setForm((prev) => ({ ...prev, inTime: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Out time</label>
              <input type="time" value={form.outTime} onChange={(e) => setForm((prev) => ({ ...prev, outTime: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
              <textarea value={form.remarks} onChange={(e) => setForm((prev) => ({ ...prev, remarks: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2" rows="3" placeholder="Add remarks for the attendance mark or regularization" />
            </div>
            <div className="md:col-span-2 flex gap-3">
              <button type="submit" disabled={!statusOptions.length || isAuditor} className="px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] disabled:opacity-50">Save attendance</button>
              <button type="button" onClick={handleRegularization} disabled={!statusOptions.length || isAuditor} className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">Request regularization</button>
            </div>
            {renderPolicyThresholds()}
            {!statusOptions.length && <p className="md:col-span-2 text-sm text-amber-800">Attendance status codes are not configured for this site and date.</p>}
          </form>
        </div>
      )}
      {tab === 'approvals' && renderApprovals()}
      {tab === 'import' && canImport && renderImport()}
      {tab === 'history' && canImport && renderImportHistory()}
    </div>
  );
}

export default AttendancePage;
